import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { BabIcon } from 'src/domains/layouts/shared/assets/icons/BabIcon';
import { CheckSingleIcon } from 'src/domains/layouts/shared/assets/icons/CheckSingleIcon';
import { FilterSecondaryPropsType } from 'src/domains/layouts/webview/components/filtersSecondary/FilterSecondary';
import { withConfig } from 'src/withConfig';

// interface FilterSecondaryStatePropsType {
//     isActive?: boolean;
//     isDisabled?: boolean;
//     filterType: 'default' | 'badge' | 'dropdown' | 'bet-builder';
// }

type FilterSecondaryStatePropsType = Pick<FilterSecondaryPropsType, 'isActive' | 'isDisabled' | 'filterType'>

const setFilterSecondaryColors = (theme: EmotionTheme, isActive?: boolean): SerializedStyles => {
    if (isActive === true) {
        return css`
            [class$='FiltersSecondaryListSecondRow'] & {
                background: ${theme.star.unified.filtersColors.secondary.bgColorSecondary};
                color: ${theme.star.unified.filtersColors.secondary.txtColorSecondary};
            }

            [class$='FiltersTertiaryList'] & {
                background: ${theme.star.unified.filtersColors.tertiary.bgColorSecondary};
                color: ${theme.star.unified.filtersColors.tertiary.txtColorSecondary};
            }

            background: ${theme.star.unified.filtersColors.primary.bgColorSecondary};
            color: ${theme.star.unified.filtersColors.primary.txtColorSecondary};
        `;
    }

    return css`
        [class$='FiltersSecondaryListSecondRow'] & {
            background: ${theme.star.unified.filtersColors.secondary.bgColor};
            color: ${theme.star.unified.filtersColors.secondary.txtColor};
        }

        [class$='FiltersTertiaryList'] & {
            background: ${theme.star.unified.filtersColors.tertiary.bgColor};
            color: ${theme.star.unified.filtersColors.tertiary.txtColor};
        }

        background: ${theme.star.unified.filtersColors.primary.bgColor};
        color: ${theme.star.unified.filtersColors.primary.txtColor};
    `;
};

export const FiltersSecondaryWrapper = withConfig(theme => styled('div', { label: 'FiltersSecondaryWrapper' })`
    background-color: ${theme.star.unified.filtersColors.primary.bgWrapperColor};
    position: relative;
`);

export const FiltersSecondaryList = styled('div', { label: 'FiltersSecondaryList' })`
    display: flex;
    flex-flow: row nowrap;
    gap: 8px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding: 8px 12px;

    &::-webkit-scrollbar {
        display: none;
    }
`;

export const FiltersTertiaryList = withConfig(theme => styled(FiltersSecondaryList, { label: 'FiltersTertiaryList' })`
    background: ${theme.star.unified.filtersColors.tertiary.bgWrapperColor};
`);

export const FiltersSecondaryListSecondRow = withConfig(theme => styled(FiltersSecondaryList, { label: 'FiltersSecondaryListSecondRow' })`
    background: ${theme.star.unified.filtersColors.secondary.bgWrapperColor};
`);

export const FilterSecondaryButton = withConfig(theme => styled('button', { label: 'FilterSecondaryButton' })<FilterSecondaryStatePropsType>`
    ${({ isActive }): SerializedStyles => setFilterSecondaryColors(theme, isActive)};
    align-items: center;
    justify-content: center;
    border-radius: ${theme.star.radius.circular};
    border-style: none;
    cursor: ${({ isDisabled }): string => (isDisabled === true ? 'not-allowed' : 'pointer')};
    display: inline-flex;
    flex: 0 0 auto;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.xMedium};
    gap: 4px;
    height: 32px;
    line-height: 1.3334;
    opacity: ${({ isDisabled }): number => (isDisabled === true ? 0.5 : 1)};
    padding: ${({ filterType = 'default' }): string => filterType === 'default'
        ? `${theme.star.padding.medium} ${theme.star.padding.xMedium}` 
        : filterType === 'bet-builder'
            ? `${theme.star.padding.medium} ${theme.star.padding.xMedium} ${theme.star.padding.medium} ${theme.star.padding.medium}`
            : `${theme.star.padding.medium} ${theme.star.padding.medium} ${theme.star.padding.medium} ${theme.star.padding.xMedium}`};
    user-select: none;
    white-space: nowrap;
`);

export const FilterSecondaryBadge = withConfig(theme => styled('span', { label: 'FilterSecondaryBadge' })<Omit<FilterSecondaryStatePropsType, 'filterType'>>`
    align-items: center;
    background: ${({ isActive }): string =>
        isActive === true
            ? theme.star.unified.filtersColors.badge.bgColorSecondary
            : theme.star.unified.filtersColors.badge.bgColor};
    border-radius: ${theme.star.radius.circular};
    color: ${({ isActive }): string =>
        isActive === true
            ? theme.star.unified.filtersColors.badge.txtColorSecondary
            : theme.star.unified.filtersColors.badge.txtColor};
    display: inline-flex;
    flex: 0 0 auto;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.xMedium};
    height: 16px;
    justify-content: center;
    line-height: 1;
    min-width: 16px;
    padding: 0 ${theme.star.padding.small};

    [class$='FiltersTertiaryList'] & {
        background: ${({ isActive }): string =>
        isActive === true
            ? theme.star.unified.filtersColors.tertiary.bgColorTertiary
            : theme.star.unified.filtersColors.tertiary.bgColorQuaternary};
        color: ${({ isActive }): string =>
            isActive === true
                ? theme.star.unified.filtersColors.tertiary.txtColorTertiary
                : theme.star.unified.filtersColors.tertiary.txtColorQuaternary};
    }
`);

interface FilterSecondaryChevronPropsType {
    isOpen?: boolean;
}

export const FilterSecondaryChevron = styled('span', { label: 'FilterSecondaryChevron' })<FilterSecondaryChevronPropsType>`
    border-color: currentcolor transparent transparent transparent;
    border-style: solid;
    border-width: 4px 4px 0 4px;
    display: block;
    flex: 0 0 auto;
    height: 0;
    margin-left: 2px;
    transform: ${({ isOpen }): string => (isOpen === true ? 'scaleY(-1)' : 'scaleY(1)')};
    transition: transform 0.2s ease;
    width: 0;
`;

export const FilterSecondaryDropdownMenu = withConfig(theme => styled('div', { label: 'FilterSecondaryDropdownMenu' })`
    background-color: ${theme.star.unified.filtersColors.dropdown.bgColor};
    border-radius: ${theme.star.radius.medium};
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.32);
    margin-top: 4px;
    min-width: 100%;
    overflow: hidden;
    position: absolute;
    top: 100%;
    z-index: ${theme.star.zIndexGlobal.above};
`);

interface FilterSecondaryDropdownMenuFixedPropsType {
    top: number;
    left: number;
    minWidth: number;
}

export const CheckIconWrapper = styled(CheckSingleIcon, { label: 'CheckIconWrapper' })`
    width: 15px;
    margin-left: auto;
    fill: currentcolor;
`;

export const FilterSecondaryDropdownMenuFixed = withConfig(theme => styled('div', { label: 'FilterSecondaryDropdownMenuFixed' })<FilterSecondaryDropdownMenuFixedPropsType>`
    background-color: ${theme.star.unified.filtersColors.dropdown.bgColor};
    border-radius: ${theme.star.radius.small};
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.32);
    left: ${({ left }): number => left}px;
    min-width: ${({ minWidth }): number => minWidth}px;
    overflow: hidden;
    position: fixed;
    top: ${({ top }): number => top}px;
    z-index: ${theme.star.zIndexGlobal.above};
`);

export const FilterSecondaryDropdownItem = withConfig(theme => styled('button', { label: 'FilterSecondaryDropdownItem' })<{ isSelected?: boolean }>`
    background: ${({ isSelected }): string =>
        isSelected === true
            ? theme.star.unified.filtersColors.dropdown.bgColorSecondary
            : 'transparent'};
    border: 0;
    color: ${({ isSelected }): string =>
        isSelected === true
            ? theme.star.unified.filtersColors.dropdown.txtColorSecondary
            : theme.star.unified.filtersColors.dropdown.txtColor};
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.regular};
    line-height: 1.4286;
    height: 36px;
    padding: 8px 16px 8px 20px;
    text-align: left;
    width: 100%;

    &:hover {
        background: ${theme.star.unified.filtersColors.dropdown.bgColorSecondary};
        color: ${theme.star.unified.filtersColors.dropdown.txtColorSecondary};
    }
`);

export const FilterSecondaryDropdownWrapper = styled('div', { label: 'FilterSecondaryDropdownWrapper' })`
    position: relative;
`;

export const FilterSecondaryBetBuilderIcon = styled(BabIcon, { label: 'FilterSecondaryBetBuilderIcon' })`
    fill: currentcolor;
    height: 16px;
    width: 16px;
`;
